// ─── UnifiedComplementsEditor ──────────────────────────────────────────────
// Editor de complementos de un producto compartido entre:
//   - Editar productos en lista (BulkProductEditor → tab Complementos)
//   - Editar/crear producto individual (AddComplements → modal fullscreen)
//
// Diseño en 2 paneles:
//   ── PANEL CONFIG (centro, flex) ──
//     - Alérgenos (chips toggleables)
//     - Toggle "Sistema de recogida simple"
//     - Barra creación inline: nueva categoría / nuevo producto
//     - Un Accordion por categoría con productos activos:
//         · Flechas ↑↓ para reordenar el grupo
//         · Checkbox master + nombre + counter checked/total
//         · Switch obligatorio + max selección + curso (si master ON)
//         · Grid de productos toggleables con precio incremental
//         · Bulk price + botón nuevo-en-esta-categoría
//     - Complementos personalizados (id null) del escaneado IA
//
//   ── PANEL PREVIEW (dcha, 300px, opcional) ──
//     - Simulación de cómo verá el cliente el producto
//     - Grupos con chips wrap + chip curso + chip obligatorio/opcional
//
// Extraído de BulkProductEditor.js para reusar en AddComplements y matar
// duplicación del editor viejo (GroupBrowserDialog master-detail).

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ArrowDownward,
  ArrowUpward,
  ExpandMore,
  Visibility,
} from "@mui/icons-material";
import { Icon } from "@iconify/react";
import plusFill from "@iconify-icons/eva/plus-fill";
import trashFill from "@iconify-icons/eva/trash-outline";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import AllergenIcon from "src/utils/AllergenIcon";
import { AddCategory, getTags } from "src/redux/slices/categories";
import { handleChangeLastCategorySelectedWhenCreate } from "src/redux/slices/product";
import SimpleAddProductModal from "../product-new/Simple/SimpleAddProductModal";

const ALL_ALLERGENS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

let __tempSeq = 0;
export const nextTempKey = () => `tmp-c-${++__tempSeq}-${Date.now()}`;

// ── normalizeComplement ────────────────────────────────────────────────────
// Shape estable de un complemento en el editor. Compartido con BulkEditor.

export function normalizeComplement(input = {}) {
  return {
    tempKey: input.tempKey || nextTempKey(),
    id: input.id ?? null,
    nombre: input.nombre ?? "",
    precio: input.precio ?? 0,
    isMandatory: !!input.isMandatory,
    maxQuantity: input.maxQuantity ?? 1,
    idCategory: input.idCategory ?? null,
    newCategoryName: input.newCategoryName ?? "",
    // Etiqueta de curso del GRUPO (misma categoría dentro del compuesto
    // = misma etiqueta). Persistida en `Complementos.CourseLabelId`.
    courseLabelId: input.courseLabelId ?? null,
  };
}

// ── buildTemplateFromRow ───────────────────────────────────────────────────
// Convierte la lista plana row.complementos en un grid editable: por cada
// categoría con productos, marcamos cuáles están seleccionados como
// complemento (checked) con su precio incremental, y guardamos por GRUPO los
// flags isMandatory/maxQuantity/courseLabelId.

export function buildTemplateFromRow(
  row,
  activeCategories,
  activeProducts,
  productById,
) {
  const groups = {};
  const orderHints = {};
  const customComplementos = [];

  activeCategories.forEach((cat) => {
    const productsOfCat = activeProducts.filter(
      (p) => p.idCategory === cat.id,
    );
    if (productsOfCat.length === 0) return;
    groups[cat.id] = {
      isMandatory: false,
      maxQuantity: 1,
      courseLabelId: null,
      products: {},
    };
    productsOfCat.forEach((p) => {
      groups[cat.id].products[p.id] = {
        checked: false,
        precio: 0,
        tempKey: null,
        nombre: p.nombre,
      };
    });
  });

  let nextOrder = 0;
  (row.complementos || []).forEach((c) => {
    if (c.id == null) {
      customComplementos.push(c);
      return;
    }
    const linked = productById?.[c.id];
    const catId = linked?.idCategory;
    if (catId == null || !groups[catId]) {
      customComplementos.push(c);
      return;
    }
    const slot = groups[catId].products[c.id] || {
      checked: false,
      precio: 0,
      tempKey: null,
      nombre: c.nombre,
    };
    slot.checked = true;
    slot.precio = Number(c.precio) || 0;
    slot.tempKey = c.tempKey;
    groups[catId].products[c.id] = slot;
    if (orderHints[catId] == null) {
      orderHints[catId] = nextOrder++;
      groups[catId].isMandatory = !!c.isMandatory;
      groups[catId].maxQuantity = c.maxQuantity || 1;
      groups[catId].courseLabelId = c.courseLabelId ?? null;
    }
  });

  const allCatIds = Object.keys(groups).map(Number);
  const used = allCatIds
    .filter((id) => orderHints[id] != null)
    .sort((a, b) => orderHints[a] - orderHints[b]);
  const unused = allCatIds.filter((id) => orderHints[id] == null);
  const groupOrder = [...used, ...unused];

  return { templateGroups: groups, groupOrder, customComplementos };
}

// ── flattenTemplate ────────────────────────────────────────────────────────

export function flattenTemplate(templateGroups, groupOrder, customComplementos) {
  const out = [];
  groupOrder.forEach((catId) => {
    const group = templateGroups[catId];
    if (!group) return;
    Object.entries(group.products).forEach(([pidStr, state]) => {
      if (!state || !state.checked) return;
      const id = Number(pidStr);
      out.push(
        normalizeComplement({
          tempKey: state.tempKey || nextTempKey(),
          id,
          nombre: state.nombre || "",
          precio: Number(state.precio) || 0,
          isMandatory: group.isMandatory,
          maxQuantity: group.maxQuantity,
          courseLabelId: group.courseLabelId ?? null,
        }),
      );
    });
  });
  (customComplementos || []).forEach((c) => out.push(c));
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Constantes SX estables — evita regeneración de clases por render
// ═══════════════════════════════════════════════════════════════════════════

const SX_ACC_INACTIVE = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "6px !important",
  mb: 0.5,
  "&:before": { display: "none" },
};
const SX_ACC_ACTIVE = {
  ...SX_ACC_INACTIVE,
  borderColor: "primary.light",
  bgcolor: "primary.lighter",
};
const SX_ACC_HIGHLIGHT = {
  ...SX_ACC_INACTIVE,
  borderColor: "warning.main",
  boxShadow: "0 0 0 2px rgba(255,167,38,0.35)",
};
const SX_ACC_TRANS = { unmountOnExit: true };
const SX_ACC_EXPAND_ICON = { fontSize: 18 };
const SX_ACC_SUMMARY = {
  minHeight: 36,
  px: 0.75,
  "&.Mui-expanded": { minHeight: 36 },
  "& .MuiAccordionSummary-content": {
    my: 0,
    alignItems: "center",
    "&.Mui-expanded": { my: 0 },
  },
  "& .MuiAccordionSummary-expandIconWrapper": { p: 0.25 },
};
const SX_ACC_HEADER = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  flex: 1,
  mr: 0.5,
};
const SX_ACC_ARROW_BOX = { display: "flex", alignItems: "center" };
const SX_ACC_TOOL_BOX_MR025 = {
  display: "flex",
  alignItems: "center",
  mr: 0.25,
};
const SX_IB_P025 = { p: 0.25 };
const SX_ARROW_ICON = { fontSize: 14 };
const SX_BADGE_BASE = {
  width: 18,
  height: 18,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: 10,
  fontWeight: 700,
};
const SX_BADGE_INACTIVE = {
  ...SX_BADGE_BASE,
  bgcolor: "grey.300",
  color: "text.secondary",
};
const SX_BADGE_ACTIVE = {
  ...SX_BADGE_BASE,
  bgcolor: "primary.main",
  color: "white",
};
const SX_ACC_TITLE = { flex: 1, minWidth: 0 };
const SX_ACC_CHIP_COUNT = { height: 18, fontSize: 10, mr: 0.5 };
const SX_MAX_TEXTFIELD = {
  width: 44,
  "& .MuiOutlinedInput-root": { borderRadius: 1 },
};
const SX_MAX_INPUTPROPS = {
  min: 1,
  style: { textAlign: "center", padding: "3px 4px", fontSize: 12 },
};
const SX_ACC_DETAILS = { pt: 0.5, px: 1, pb: 1 };
const SX_BULK_BAR = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
  mb: 0.75,
  pb: 0.75,
  borderBottom: "1px dashed",
  borderColor: "divider",
};
const SX_BULK_INPUT = { width: 90 };
const SX_BULK_INPUT_PROPS = {
  min: 0,
  step: 0.05,
  style: { padding: "3px 6px", fontSize: 12 },
};
const SX_BULK_END_ADORN_SX = { "& p": { fontSize: 11, color: "text.secondary" } };
const SX_PLUS_BTN = {
  ml: "auto",
  fontSize: 11,
  textTransform: "none",
  color: "primary.main",
};

const stopPropagation = (e) => e.stopPropagation();

const SX_PROD_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
  gap: 0.5,
};
const SX_PROD_ITEM_UNCHECKED = {
  display: "flex",
  alignItems: "center",
  gap: 0.25,
  px: 0.5,
  py: 0.25,
  borderRadius: 1,
  transition: "background-color 0.15s",
};
const SX_PROD_ITEM_CHECKED = {
  ...SX_PROD_ITEM_UNCHECKED,
  bgcolor: "action.selected",
};
const SX_PROD_NAME = { flex: 1, minWidth: 0, fontSize: 13 };
const SX_PROD_PRICE_INPUT = {
  width: 70,
  "& .MuiOutlinedInput-root": { borderRadius: 1 },
};
const PROD_PRICE_INPUTPROPS = {
  endAdornment: (
    <InputAdornment
      position="end"
      sx={{ ml: 0, "& p": { fontSize: 11, color: "text.secondary" } }}
    >
      €
    </InputAdornment>
  ),
  inputProps: {
    type: "number",
    min: 0,
    step: 0.1,
    style: {
      padding: "3px 0 3px 4px",
      textAlign: "right",
      fontSize: 12,
      width: 38,
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// ProductComplementCell — fila producto memoizada
// ═══════════════════════════════════════════════════════════════════════════

const ProductComplementCell = memo(function ProductComplementCell({
  product,
  pState,
  onToggle,
  onChangePrice,
  catId,
}) {
  const handleToggle = useCallback(
    () => onToggle(catId, product.id),
    [onToggle, catId, product.id],
  );
  const handlePrice = useCallback(
    (e) => onChangePrice(catId, product.id, e.target.value),
    [onChangePrice, catId, product.id],
  );
  return (
    <Box sx={pState.checked ? SX_PROD_ITEM_CHECKED : SX_PROD_ITEM_UNCHECKED}>
      <Checkbox
        size="small"
        checked={pState.checked}
        onChange={handleToggle}
        sx={SX_IB_P025}
      />
      <Typography variant="body2" sx={SX_PROD_NAME} noWrap>
        {product.nombre}
      </Typography>
      <TextField
        size="small"
        placeholder="0"
        value={pState.precio}
        disabled={!pState.checked}
        onChange={handlePrice}
        InputProps={PROD_PRICE_INPUTPROPS}
        sx={SX_PROD_PRICE_INPUT}
      />
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// ConfigGroupAccordion — grupo de complementos por categoría
// ═══════════════════════════════════════════════════════════════════════════

const ConfigGroupAccordion = memo(function ConfigGroupAccordion({
  catId,
  cat,
  group,
  products,
  index,
  totalGroups,
  onMoveGroup,
  onSetGroup,
  onToggleProduct,
  onToggleAll,
  onChangePrice,
  onSetBulkPrice,
  onCreateProductHere,
  courseTags = null,
  defaultExpanded = false,
  highlight = false,
  scrollRef,
}) {
  const checkedCount = Object.values(group.products).filter(
    (p) => p.checked,
  ).length;
  const total = Object.keys(group.products).length;
  const allChecked = total > 0 && checkedCount === total;
  const someChecked = checkedCount > 0 && !allChecked;

  const handleMoveUp = () => onMoveGroup(catId, -1);
  const handleMoveDown = () => onMoveGroup(catId, +1);
  const handleMandatory = (e) =>
    onSetGroup(catId, { isMandatory: e.target.checked });
  const handleMax = (e) =>
    onSetGroup(catId, { maxQuantity: Math.max(1, Number(e.target.value) || 1) });
  const handleCourse = (e) =>
    onSetGroup(catId, {
      courseLabelId:
        e.target.value === "" || e.target.value == null
          ? null
          : Number(e.target.value),
    });
  const handleAll = (e) => {
    e.stopPropagation();
    onToggleAll(catId, allChecked);
  };

  const accSx = highlight
    ? SX_ACC_HIGHLIGHT
    : checkedCount > 0
    ? SX_ACC_ACTIVE
    : SX_ACC_INACTIVE;
  const badgeSx = checkedCount > 0 ? SX_BADGE_ACTIVE : SX_BADGE_INACTIVE;

  return (
    <div ref={scrollRef}>
      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        elevation={0}
        TransitionProps={SX_ACC_TRANS}
        sx={accSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={SX_ACC_EXPAND_ICON} />}
          sx={SX_ACC_SUMMARY}
        >
          <Box sx={SX_ACC_HEADER}>
            <Box sx={SX_ACC_ARROW_BOX} onClick={stopPropagation}>
              <IconButton
                size="small"
                onClick={handleMoveUp}
                disabled={index === 0}
                sx={SX_IB_P025}
              >
                <ArrowUpward sx={SX_ARROW_ICON} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleMoveDown}
                disabled={index === totalGroups - 1}
                sx={SX_IB_P025}
              >
                <ArrowDownward sx={SX_ARROW_ICON} />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={badgeSx}>
              {index + 1}
            </Typography>
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={someChecked}
              onClick={handleAll}
              sx={SX_IB_P025}
            />
            <Typography variant="subtitle2" sx={SX_ACC_TITLE} noWrap>
              {cat.name}
            </Typography>
            {checkedCount > 0 && (
              <Chip
                label={`${checkedCount}/${total}`}
                size="small"
                color="primary"
                sx={SX_ACC_CHIP_COUNT}
              />
            )}

            <Box onClick={stopPropagation} sx={SX_ACC_ARROW_BOX}>
              <Tooltip
                title={
                  checkedCount === 0
                    ? "Marca al menos un complemento"
                    : group.isMandatory
                    ? "Obligatorio"
                    : "Opcional"
                }
              >
                <span>
                  <Switch
                    size="small"
                    checked={group.isMandatory}
                    onChange={handleMandatory}
                    disabled={checkedCount === 0}
                  />
                </span>
              </Tooltip>
            </Box>

            <Box onClick={stopPropagation} sx={SX_ACC_TOOL_BOX_MR025}>
              <Tooltip title="Máx. selección">
                <TextField
                  size="small"
                  type="number"
                  value={group.maxQuantity}
                  onChange={handleMax}
                  disabled={checkedCount === 0}
                  inputProps={SX_MAX_INPUTPROPS}
                  sx={SX_MAX_TEXTFIELD}
                />
              </Tooltip>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={SX_ACC_DETAILS}>
          {(onSetBulkPrice || onCreateProductHere) && (
            <Box sx={SX_BULK_BAR}>
              {onSetBulkPrice && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Precio a todos los seleccionados:
                  </Typography>
                  <TextField
                    key={catId}
                    size="small"
                    type="number"
                    defaultValue=""
                    placeholder="0.00"
                    disabled={checkedCount === 0}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v === "") return;
                      onSetBulkPrice(catId, v);
                      e.target.value = "";
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.target.blur();
                      }
                    }}
                    inputProps={SX_BULK_INPUT_PROPS}
                    sx={SX_BULK_INPUT}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          sx={SX_BULK_END_ADORN_SX}
                        >
                          €
                        </InputAdornment>
                      ),
                    }}
                  />
                </>
              )}
              {onCreateProductHere && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<Icon icon={plusFill} width={14} />}
                  onClick={onCreateProductHere}
                  sx={SX_PLUS_BTN}
                >
                  Nuevo en {cat?.name || "esta categoría"}
                </Button>
              )}
              {courseTags && courseTags.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    Curso:
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={group.courseLabelId ?? ""}
                    onChange={handleCourse}
                    SelectProps={{ native: true }}
                    sx={{ minWidth: 120 }}
                  >
                    <option value="">— Ninguno —</option>
                    {courseTags
                      .slice()
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </TextField>
                </>
              )}
            </Box>
          )}

          <Box sx={SX_PROD_GRID}>
            {products.map((product) => (
              <ProductComplementCell
                key={product.id}
                product={product}
                pState={
                  group.products[product.id] || { checked: false, precio: 0 }
                }
                onToggle={onToggleProduct}
                onChangePrice={onChangePrice}
                catId={catId}
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// ProductPreviewPanel — preview cliente (opcional)
// ═══════════════════════════════════════════════════════════════════════════

const ProductPreviewPanel = memo(function ProductPreviewPanel({
  row,
  templateGroups,
  groupOrder,
  activeProducts,
  activeCategories,
  allergenSet,
}) {
  const courseTagsAll = useSelector((s) => s.categories?.tags || []);
  const courseNameById = useMemo(() => {
    const m = new Map();
    (courseTagsAll || []).forEach((t) => m.set(t.id, t.name));
    return m;
  }, [courseTagsAll]);

  const activeGroupIds = groupOrder.filter((catId) => {
    const g = templateGroups[catId];
    return g && Object.values(g.products).some((p) => p.checked);
  });

  const hasContent = activeGroupIds.length > 0 || allergenSet.size > 0;

  if (!hasContent) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "text.disabled",
          gap: 1,
          p: 2,
          textAlign: "center",
        }}
      >
        <Visibility sx={{ fontSize: 40, opacity: 0.3 }} />
        <Typography variant="caption">
          Marca complementos para ver la preview
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflow: "auto", p: 1.25 }}>
      <Paper variant="outlined" sx={{ p: 1.25, mb: 1, borderRadius: 1.5 }}>
        <Typography variant="subtitle2" noWrap>
          {row.nombre || "(sin nombre)"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {Number(row.precio || 0).toFixed(2)}€ · IVA {row.iva}%
        </Typography>
      </Paper>

      {allergenSet.size > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            alignItems: "center",
            mb: 1,
            px: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Alérgenos:
          </Typography>
          {Array.from(allergenSet).map((id) => (
            <AllergenIcon key={id} id={id} isEnabled widthSize={20} />
          ))}
        </Box>
      )}

      {activeGroupIds.map((catId) => {
        const group = templateGroups[catId];
        const cat = activeCategories.find((c) => c.id === catId);
        if (!cat || !group) return null;

        const checkedProducts = Object.entries(group.products)
          .filter(([, p]) => p.checked)
          .map(([pid, p]) => {
            const product = activeProducts.find((ap) => ap.id === Number(pid));
            return product ? { ...product, precio: p.precio } : null;
          })
          .filter(Boolean);

        return (
          <Paper
            key={catId}
            variant="outlined"
            sx={{
              mb: 1,
              borderRadius: 1.5,
              overflow: "hidden",
              borderLeft: "3px solid",
              borderLeftColor: group.isMandatory ? "error.main" : "primary.main",
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "grey.50",
                borderBottom: "1px solid",
                borderBottomColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
                noWrap
              >
                {cat.name}
              </Typography>
              <Chip
                label={group.isMandatory ? "Obligatorio" : "Opcional"}
                size="small"
                color={group.isMandatory ? "error" : "default"}
                variant="outlined"
                sx={{ height: 16, fontSize: 9 }}
              />
              {group.maxQuantity > 1 && (
                <Chip
                  label={`máx. ${group.maxQuantity}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 16, fontSize: 9 }}
                />
              )}
              {group.courseLabelId != null &&
                courseNameById.get(group.courseLabelId) && (
                  <Chip
                    label={courseNameById.get(group.courseLabelId)}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 9,
                      fontWeight: 700,
                      bgcolor: "#ecfdf5",
                      color: "#065f46",
                      border: "1px solid #a7f3d0",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                p: 0.75,
              }}
            >
              {checkedProducts.map((product) => (
                <Chip
                  key={product.id}
                  label={
                    product.precio > 0
                      ? `${product.nombre} +${Number(product.precio).toFixed(2)}€`
                      : product.nombre
                  }
                  size="small"
                  variant="outlined"
                  color={product.precio > 0 ? "primary" : "default"}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              ))}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// UnifiedComplementsEditor — componente principal
// ═══════════════════════════════════════════════════════════════════════════

export default function UnifiedComplementsEditor({
  row,
  productsAvailable,
  categoriesAvailable,
  productById,
  onReplaceComplementos,
  onUpdateRow,
  showPreview = true,
  showAlergenos = true,
  showSimplePickingToggle = true,
  initialScrollToCatId = null,
}) {
  const courseTagsAll = useSelector((s) => s.categories?.tags || []);
  const tpvLayoutRaw = useSelector(
    (s) => s.authJwt?.user?.tpvLayoutSettingsJson,
  );
  const showCoursesInEditor = useMemo(() => {
    try {
      const parsed = tpvLayoutRaw ? JSON.parse(tpvLayoutRaw) : null;
      return !!parsed?.cartWidget?.showCourseSelector;
    } catch {
      return false;
    }
  }, [tpvLayoutRaw]);
  const activeCourseTags = useMemo(
    () => (courseTagsAll || []).filter((t) => t?.isActive !== false),
    [courseTagsAll],
  );

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (showCoursesInEditor) dispatch(getTags(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCoursesInEditor]);

  // ── Creación inline ─────────────────────────────────────────────────────
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [createProductForCatId, setCreateProductForCatId] = useState(null);

  const handleCreateCategory = useCallback(async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setSavingCategory(true);
    try {
      await dispatch(
        AddCategory({
          Name: name,
          Visible: true,
          IdRestaurante: window.localStorage.getItem("userId"),
          IdCategory: null,
          Printer: -1,
          Order: 1,
        }),
      );
      enqueueSnackbar("Categoría creada", { variant: "success" });
      setNewCategoryName("");
      setCreatingCategory(false);
    } catch (_) {
      enqueueSnackbar("No se pudo crear la categoría", { variant: "error" });
    } finally {
      setSavingCategory(false);
    }
  }, [dispatch, enqueueSnackbar, newCategoryName]);

  const openCreateProduct = useCallback(
    (catId) => {
      if (catId != null) {
        dispatch(handleChangeLastCategorySelectedWhenCreate(catId));
      }
      setCreateProductForCatId(catId ?? null);
      setAddProductOpen(true);
    },
    [dispatch],
  );

  const handleProductCreatedFromModal = useCallback(
    (complemento) => {
      const targetCatId = createProductForCatId ?? complemento.idCategory;
      const newComp = normalizeComplement({
        id: complemento.idProduct,
        nombre: complemento.name,
        precio: 0,
        idCategory: targetCatId,
        isMandatory: true,
        maxQuantity: 1,
      });
      onReplaceComplementos([...(row.complementos || []), newComp]);
      setAddProductOpen(false);
      setCreateProductForCatId(null);
    },
    [createProductForCatId, onReplaceComplementos, row.complementos],
  );

  // ── Derivados ───────────────────────────────────────────────────────────
  const activeProducts = useMemo(
    () => (productsAvailable || []).filter((p) => p && p.isActive !== false),
    [productsAvailable],
  );
  const activeCategories = useMemo(() => {
    const ids = new Set(activeProducts.map((p) => p.idCategory));
    return (categoriesAvailable || []).filter((c) => ids.has(c.id));
  }, [activeProducts, categoriesAvailable]);

  const { templateGroups, groupOrder, customComplementos } = useMemo(
    () =>
      buildTemplateFromRow(row, activeCategories, activeProducts, productById),
    [row, activeCategories, activeProducts, productById],
  );

  // ── Mutaciones del template ─────────────────────────────────────────────
  const emit = useCallback(
    (newGroups, newOrder, newCustom) => {
      const next = flattenTemplate(
        newGroups || templateGroups,
        newOrder || groupOrder,
        newCustom || customComplementos,
      );
      onReplaceComplementos(next);
    },
    [templateGroups, groupOrder, customComplementos, onReplaceComplementos],
  );

  const setGroup = useCallback(
    (catId, patch) => {
      const newGroups = {
        ...templateGroups,
        [catId]: { ...templateGroups[catId], ...patch },
      };
      emit(newGroups);
    },
    [templateGroups, emit],
  );

  const setGroupProducts = useCallback(
    (catId, productsPatch) => {
      const newGroups = {
        ...templateGroups,
        [catId]: {
          ...templateGroups[catId],
          products: productsPatch,
        },
      };
      emit(newGroups);
    },
    [templateGroups, emit],
  );

  const toggleProductInGroup = useCallback(
    (catId, productId) => {
      const group = templateGroups[catId];
      if (!group) return;
      const cur = group.products[productId] || { checked: false, precio: 0 };
      const nextProducts = {
        ...group.products,
        [productId]: { ...cur, checked: !cur.checked },
      };
      setGroupProducts(catId, nextProducts);
    },
    [templateGroups, setGroupProducts],
  );

  const toggleAllInGroup = useCallback(
    (catId, allChecked) => {
      const group = templateGroups[catId];
      if (!group) return;
      const nextProducts = {};
      Object.entries(group.products).forEach(([pid, st]) => {
        nextProducts[pid] = { ...st, checked: !allChecked };
      });
      setGroupProducts(catId, nextProducts);
    },
    [templateGroups, setGroupProducts],
  );

  const setProductPrice = useCallback(
    (catId, productId, precio) => {
      const group = templateGroups[catId];
      if (!group) return;
      const cur = group.products[productId] || { checked: false, precio: 0 };
      setGroupProducts(catId, {
        ...group.products,
        [productId]: { ...cur, precio: Number(precio) || 0 },
      });
    },
    [templateGroups, setGroupProducts],
  );

  const setBulkPriceInGroup = useCallback(
    (catId, precio) => {
      const group = templateGroups[catId];
      if (!group) return;
      const num = precio === "" ? 0 : Number(precio) || 0;
      const next = {};
      Object.entries(group.products).forEach(([pid, st]) => {
        next[pid] = st.checked ? { ...st, precio: num } : st;
      });
      setGroupProducts(catId, next);
    },
    [templateGroups, setGroupProducts],
  );

  const moveGroup = useCallback(
    (catId, dir) => {
      const idx = groupOrder.indexOf(catId);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= groupOrder.length) return;
      const newOrder = Array.from(groupOrder);
      [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
      emit(null, newOrder);
    },
    [groupOrder, emit],
  );

  // ── Custom complementos (id null, del OCR IA) ───────────────────────────
  const updateCustomComp = useCallback(
    (tempKey, patch) => {
      onReplaceComplementos(
        (row.complementos || []).map((c) =>
          c.tempKey === tempKey ? { ...c, ...patch } : c,
        ),
      );
    },
    [row.complementos, onReplaceComplementos],
  );

  const removeCustomComp = useCallback(
    (tempKey) => {
      onReplaceComplementos(
        (row.complementos || []).filter((c) => c.tempKey !== tempKey),
      );
    },
    [row.complementos, onReplaceComplementos],
  );

  const linkableProductOptions = useMemo(
    () =>
      activeProducts
        .map((p) => ({
          id: p.id,
          label: p.nombre,
          categoryName:
            (categoriesAvailable || []).find((c) => c.id === p.idCategory)
              ?.name || "Sin categoría",
          precio: p.precio,
        }))
        .sort(
          (a, b) =>
            (a.categoryName || "").localeCompare(b.categoryName || "") ||
            (a.label || "").localeCompare(b.label || ""),
        ),
    [activeProducts, categoriesAvailable],
  );

  const categoryOptionsForCustom = useMemo(
    () =>
      (categoriesAvailable || [])
        .filter((c) => c && c.name)
        .map((c) => ({ id: c.id, label: c.name })),
    [categoriesAvailable],
  );

  // ── Alérgenos ────────────────────────────────────────────────────────────
  const allergenSet = useMemo(
    () => new Set(row.alergenos || []),
    [row.alergenos],
  );
  const toggleAllergen = (id) => {
    const cur = row.alergenos || [];
    const next = cur.includes(id)
      ? cur.filter((a) => a !== id)
      : [...cur, id];
    onUpdateRow({ alergenos: next });
  };

  // ── Auto-scroll al grupo indicado al montar ─────────────────────────────
  const scrollRefs = useRef({});
  useEffect(() => {
    if (initialScrollToCatId == null) return;
    const el = scrollRefs.current[initialScrollToCatId];
    if (el && typeof el.scrollIntoView === "function") {
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: "smooth", block: "start" }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
      {/* PANEL CONFIG */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRight: showPreview ? "1px solid" : "none",
          borderColor: "divider",
        }}
      >
        <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
          {showAlergenos && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                border: "1px solid",
                borderColor:
                  allergenSet.size > 0 ? "warning.light" : "divider",
                borderRadius: 2,
                bgcolor: allergenSet.size > 0 ? "warning.lighter" : undefined,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Alérgenos
                {allergenSet.size > 0 && (
                  <Chip
                    label={allergenSet.size}
                    size="small"
                    color="warning"
                    sx={{ ml: 1, height: 18, fontSize: 11 }}
                  />
                )}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {ALL_ALLERGENS.map((id) => (
                  <AllergenIcon
                    key={id}
                    id={id}
                    isEnabled={allergenSet.has(id)}
                    widthSize={28}
                    onClick={() => toggleAllergen(id)}
                  />
                ))}
              </Box>
              {allergenSet.size > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    display: "block",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => onUpdateRow({ alergenos: [] })}
                >
                  Limpiar selección
                </Typography>
              )}
            </Box>
          )}

          {showSimplePickingToggle && (
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2">
                  Sistema de recogida simple
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Un solo pase para elegir todos los complementos, sin
                  agruparlos en fases.
                </Typography>
              </Box>
              <Switch
                checked={!!row.useSimplePickingSystem}
                onChange={(e) =>
                  onUpdateRow({ useSimplePickingSystem: e.target.checked })
                }
              />
            </Box>
          )}

          {/* Barra creación inline */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            {creatingCategory ? (
              <>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Nombre de la nueva categoría"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      newCategoryName.trim() &&
                      !savingCategory
                    ) {
                      e.preventDefault();
                      handleCreateCategory();
                    }
                  }}
                  disabled={savingCategory}
                  sx={{ minWidth: 260 }}
                />
                <LoadingButton
                  size="small"
                  variant="contained"
                  loading={savingCategory}
                  disabled={!newCategoryName.trim()}
                  onClick={handleCreateCategory}
                >
                  Crear
                </LoadingButton>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setCreatingCategory(false);
                    setNewCategoryName("");
                  }}
                  disabled={savingCategory}
                >
                  Cancelar
                </Button>
              </>
            ) : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon icon={plusFill} width={16} />}
                onClick={() => setCreatingCategory(true)}
              >
                Nueva categoría
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon icon={plusFill} width={16} />}
              onClick={() => openCreateProduct(null)}
            >
              Nuevo producto
            </Button>
          </Box>

          {addProductOpen && (
            <SimpleAddProductModal
              open={addProductOpen}
              handleClose={() => {
                setAddProductOpen(false);
                setCreateProductForCatId(null);
              }}
              onSelect={handleProductCreatedFromModal}
            />
          )}

          {/* Grupos */}
          {groupOrder.length === 0 ? (
            <Alert severity="info">
              Aún no hay categorías con productos activos. Crea una categoría
              y un producto con los botones de arriba para empezar.
            </Alert>
          ) : (
            <Box>
              {groupOrder.map((catId, index) => {
                const group = templateGroups[catId];
                const cat = activeCategories.find((c) => c.id === catId);
                if (!group || !cat) return null;
                const productsOfCat = activeProducts.filter(
                  (p) => p.idCategory === catId,
                );
                if (productsOfCat.length === 0) return null;

                const isTarget =
                  initialScrollToCatId != null &&
                  initialScrollToCatId === catId;

                return (
                  <ConfigGroupAccordion
                    key={catId}
                    catId={catId}
                    cat={cat}
                    group={group}
                    products={productsOfCat}
                    index={index}
                    totalGroups={groupOrder.length}
                    onMoveGroup={moveGroup}
                    onSetGroup={setGroup}
                    onToggleProduct={toggleProductInGroup}
                    onToggleAll={toggleAllInGroup}
                    onChangePrice={setProductPrice}
                    onSetBulkPrice={setBulkPriceInGroup}
                    onCreateProductHere={() => openCreateProduct(catId)}
                    courseTags={
                      showCoursesInEditor ? activeCourseTags : null
                    }
                    defaultExpanded={isTarget}
                    highlight={isTarget}
                    scrollRef={(el) => {
                      scrollRefs.current[catId] = el;
                    }}
                  />
                );
              })}
            </Box>
          )}

          {/* Complementos personalizados (id null, del OCR IA) */}
          {customComplementos.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">
                  Complementos personalizados
                </Typography>
                <Chip
                  size="small"
                  label={`${customComplementos.length} pendiente${customComplementos.length !== 1 ? "s" : ""}`}
                  color="warning"
                  sx={{ height: 18, fontSize: 10 }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Se crearán como productos nuevos al guardar. Selecciona en el
                desplegable un producto existente para vincular en lugar de
                crear.
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 0.75 }}>
                {customComplementos.map((c) => (
                  <Box
                    key={c.tempKey}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      py: 0.5,
                      borderBottom: "1px dashed",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Autocomplete
                      size="small"
                      freeSolo
                      options={linkableProductOptions}
                      groupBy={(o) => o.categoryName}
                      value={c.nombre || ""}
                      getOptionLabel={(o) =>
                        typeof o === "string" ? o : o?.label || ""
                      }
                      isOptionEqualToValue={(o, v) =>
                        typeof o === "string" || typeof v === "string"
                          ? o === v
                          : o?.id === v?.id
                      }
                      onChange={(_, v) => {
                        if (!v) {
                          updateCustomComp(c.tempKey, { nombre: "" });
                          return;
                        }
                        if (typeof v === "string") {
                          updateCustomComp(c.tempKey, { nombre: v });
                          return;
                        }
                        updateCustomComp(c.tempKey, {
                          id: v.id,
                          nombre: v.label,
                          idCategory: null,
                          newCategoryName: "",
                        });
                      }}
                      onInputChange={(_, v, reason) => {
                        if (reason === "input")
                          updateCustomComp(c.tempKey, { nombre: v });
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.categoryName}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Nombre o vincular a existente"
                        />
                      )}
                      sx={{ flex: 2, minWidth: 160 }}
                    />
                    <Autocomplete
                      size="small"
                      freeSolo
                      options={categoryOptionsForCustom}
                      value={
                        c.idCategory
                          ? categoryOptionsForCustom.find(
                              (o) => o.id === c.idCategory,
                            ) || null
                          : c.newCategoryName || null
                      }
                      getOptionLabel={(o) =>
                        typeof o === "string" ? o : o?.label || ""
                      }
                      isOptionEqualToValue={(o, v) =>
                        typeof o === "string" || typeof v === "string"
                          ? o === v
                          : o?.id === v?.id
                      }
                      onChange={(_, v) => {
                        if (!v) {
                          updateCustomComp(c.tempKey, {
                            idCategory: null,
                            newCategoryName: "",
                          });
                          return;
                        }
                        if (typeof v === "string") {
                          updateCustomComp(c.tempKey, {
                            idCategory: null,
                            newCategoryName: v,
                          });
                          return;
                        }
                        updateCustomComp(c.tempKey, {
                          idCategory: v.id,
                          newCategoryName: "",
                        });
                      }}
                      onInputChange={(_, v, reason) => {
                        if (reason === "input")
                          updateCustomComp(c.tempKey, {
                            idCategory: null,
                            newCategoryName: v,
                          });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Categoría"
                        />
                      )}
                      sx={{ flex: 1, minWidth: 120 }}
                    />
                    <TextField
                      variant="standard"
                      type="number"
                      value={c.precio}
                      onChange={(e) =>
                        updateCustomComp(c.tempKey, {
                          precio: e.target.value,
                        })
                      }
                      inputProps={{ step: 0.05, min: 0 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">+</InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">€</InputAdornment>
                        ),
                      }}
                      sx={{ width: 90 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeCustomComp(c.tempKey)}
                    >
                      <Icon icon={trashFill} width={16} color="#d32f2f" />
                    </IconButton>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </Box>
      </Box>

      {/* PANEL PREVIEW */}
      {showPreview && (
        <Box
          sx={{
            width: 300,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="subtitle2">Preview</Typography>
            <Typography variant="caption" color="text.secondary">
              Así verá el cliente este producto
            </Typography>
          </Box>
          <ProductPreviewPanel
            row={row}
            templateGroups={templateGroups}
            groupOrder={groupOrder}
            activeProducts={activeProducts}
            activeCategories={activeCategories}
            allergenSet={allergenSet}
          />
        </Box>
      )}
    </Box>
  );
}
